/**
 * Admin Authentication Helpers
 *
 * Defense-in-depth gating for admin UI:
 * 1. User must be authenticated
 * 2. User must have staff role (admin/moderator/service_role)
 * 3. User must be explicitly allowlisted via env var
 * 4. Admin UI must be enabled via env var
 *
 * Note: Backend also enforces IP allowlist independently.
 */

import type { User } from '@supabase/supabase-js';

/**
 * Staff roles that have admin access (mirrors backend isStaff logic)
 */
const STAFF_ROLES = ['admin', 'moderator', 'service_role'] as const;

/**
 * Check if a user has a staff role.
 * Mirrors worker/lib/admin-config.js isStaff() semantics.
 */
export function isStaffUser(user: User | null): boolean {
  if (!user) return false;

  // Check app_metadata.role (primary) or user_metadata.role (fallback)
  const role =
    (user.app_metadata?.role as string) ||
    (user.user_metadata?.role as string) ||
    '';

  return STAFF_ROLES.includes(role as typeof STAFF_ROLES[number]);
}

/**
 * Parse admin UI allowlisted user IDs from env var.
 * Format: comma-separated UUIDs
 */
function parseAllowlistedUserIds(): Set<string> {
  const raw = import.meta.env.VITE_ADMIN_UI_ALLOWLIST_USER_IDS || '';
  const ids = raw
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);
  return new Set(ids);
}

/**
 * Check if admin UI is enabled via env var.
 * Must be exactly 'true' to be enabled.
 */
function isAdminUiEnabled(): boolean {
  return import.meta.env.VITE_ADMIN_UI_ENABLED === 'true';
}

/**
 * Check if a user is allowed to access the admin UI.
 *
 * Requirements (all must be true):
 * 1. VITE_ADMIN_UI_ENABLED === 'true'
 * 2. User is authenticated
 * 3. User has staff role
 * 4. User ID is in VITE_ADMIN_UI_ALLOWLIST_USER_IDS
 */
export function isAdminUiAllowed(user: User | null): boolean {
  // Check 1: Admin UI must be enabled
  if (!isAdminUiEnabled()) {
    return false;
  }

  // Check 2: User must be authenticated
  if (!user) {
    return false;
  }

  // Check 3: User must be staff
  if (!isStaffUser(user)) {
    return false;
  }

  // Check 4: User ID must be in allowlist
  const allowlist = parseAllowlistedUserIds();
  if (!allowlist.has(user.id)) {
    return false;
  }

  return true;
}

/**
 * Get admin access denied reason (for debugging/logging).
 * Returns null if access is allowed.
 */
export function getAdminAccessDeniedReason(user: User | null): string | null {
  if (!isAdminUiEnabled()) {
    return 'Admin UI is not enabled';
  }

  if (!user) {
    return 'User is not authenticated';
  }

  if (!isStaffUser(user)) {
    return 'User does not have staff role';
  }

  const allowlist = parseAllowlistedUserIds();
  if (!allowlist.has(user.id)) {
    return 'User ID is not in admin allowlist';
  }

  return null; // Access allowed
}
